import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, XCircle, Users, Calendar, Mail, Phone, User, RefreshCw } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions } from './usePermissions';

const DuplicateDetailPage = ({ config, currentUser }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(currentUser);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const t = config?.terminology || {
    client: { singular: 'Patient', plural: 'Patients' }
  };

  // Elevated merge authority - mirrors the backend check in mergeDuplicates:
  // legacy SuperUsers group OR both clinical delete permissions via RBAC
  const isSuperUser = currentUser?.groups?.includes('SuperUsers') ||
    (hasPermission('patients.medications.delete') && hasPermission('patients.protocols.delete'));

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getLatestDuplicateScan();
      // Filter out groups that have been resolved (merged or marked as not duplicate)
      const unresolvedGroups = (response.groups || []).filter(g => !g.resolved);
      setDuplicateGroups(unresolvedGroups);
    } catch (err) {
      console.error('Failed to load duplicates:', err);
      setError('Failed to load duplicate groups');
    } finally {
      setLoading(false);
    }
  };

  const triggerScan = async () => {
    try {
      setLoading(true);
      setError(null);
      await ApiService.triggerDuplicateScan();
      // Reload duplicates after scan completes
      await loadDuplicates();
    } catch (err) {
      console.error('Failed to trigger scan:', err);
      setError('Failed to trigger duplicate scan: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = async (group) => {
    if (expandedGroup?.groupId === group.groupId) {
      setExpandedGroup(null);
    } else {
      // Just expand the group - don't load meds/protocols yet
      // They'll be loaded when user clicks "Merge" if needed
      setExpandedGroup(group);
    }
  };

  const checkCanMerge = (group) => {
    if (!group.patients) return { canMerge: false, reason: 'No patient data' };

    // Note: Backend will check for different medications/protocols
    // and require SuperUser permission if needed
    return { canMerge: true };
  };

  const handleMerge = async (groupId, primaryPatientId) => {
    const group = duplicateGroups.find(g => g.groupId === groupId);
    const mergeCheck = checkCanMerge(expandedGroup || group);

    if (!mergeCheck.canMerge) {
      setModalMessage(mergeCheck.reason);
      setShowErrorModal(true);
      return;
    }

    try {
      await ApiService.mergeDuplicates(groupId, primaryPatientId);
      setModalMessage('Duplicate records have been merged successfully. The primary record has been preserved.');
      setShowSuccessModal(true);
      setExpandedGroup(null);
      setSelectedAction(null);
    } catch (err) {
      console.error('Failed to merge duplicates:', err);
      setModalMessage(err.message || 'Failed to merge duplicate records. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleMarkAsNotDuplicate = async (groupId) => {
    try {
      await ApiService.markAsNotDuplicate(groupId);
      setModalMessage('Group has been marked as not duplicate and will not appear in future scans.');
      setShowSuccessModal(true);
      setExpandedGroup(null);
      setSelectedAction(null);
    } catch (err) {
      console.error('Failed to mark as not duplicate:', err);
      setModalMessage(err.message || 'Failed to update. Please try again.');
      setShowErrorModal(true);
    }
  };

  const renderMatchCriteria = (criteria) => {
    const labels = {
      exact_email: 'Exact Email Match',
      exact_phone: 'Exact Phone Match',
      exact_name: 'Exact Name Match',
      similar_name: 'Similar Name',
      same_dob: 'Same Date of Birth',
      same_email_domain: 'Same Email Domain'
    };

    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {criteria.map((criterion, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
          >
            {labels[criterion] || criterion}
          </span>
        ))}
      </div>
    );
  };

  const renderPatientCard = (patient, isExpanded, isPrimary = false) => {
    return (
      <div
        className={`p-4 border rounded-lg ${
          isPrimary ? 'border-spruce-500 bg-spruce-50' : 'border-gray-200 bg-white'
        }`}
      >
        {isPrimary && (
          <div className="mb-2">
            <span className="px-2 py-1 bg-spruce-600 text-white text-xs rounded-full">
              Primary Record
            </span>
          </div>
        )}
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User size={16} className="text-gray-500" />
            <span className="font-semibold">{patient.name}</span>
          </div>
          
          {patient.email && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail size={14} className="text-gray-400" />
              {patient.email}
            </div>
          )}
          
          {patient.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone size={14} className="text-gray-400" />
              {patient.phone}
            </div>
          )}
          
          {patient.dateOfBirth && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar size={14} className="text-gray-400" />
              {new Date(patient.dateOfBirth).toLocaleDateString()}
            </div>
          )}
          
          <div className="mt-2 text-xs text-gray-400">
            Created: {new Date(patient.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>
    );
  };

  const renderDuplicateGroup = (group, index) => {
    const isExpanded = expandedGroup?.groupId === group.groupId;
    const mergeCheck = isExpanded ? checkCanMerge(expandedGroup) : { canMerge: true };

    return (
      <div key={group.groupId} className="border border-gray-200 rounded-lg overflow-hidden">
        <div
          className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
          onClick={() => handleGroupClick(group)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-yellow-600" size={20} />
              <div>
                <h3 className="font-semibold text-gray-900">
                  Duplicate Group ({group.patients[0]?.name || 'Unknown'})
                </h3>
                <p className="text-sm text-gray-600">
                  {group.count} potential duplicates • Detected{' '}
                  {new Date(group.detectedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="text-gray-400">
              {isExpanded ? '▼' : '▶'}
            </div>
          </div>
        </div>

        {isExpanded && expandedGroup && (
          <div className="p-4 bg-white">
            {/* Show merge restriction warning */}
            {!mergeCheck.canMerge && mergeCheck.requiresSuperUser && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle size={16} />
                  <span className="text-sm font-semibold">SuperUser Required</span>
                </div>
                <p className="text-sm text-yellow-700 mt-1">
                  {mergeCheck.reason}
                </p>
              </div>
            )}

            {/* Patient comparison grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {expandedGroup.patients.map((patient, idx) => {
                // Primary record = oldest patient (earliest createdAt)
                const oldestPatient = expandedGroup.patients.reduce((oldest, current) => {
                  const oldestDate = new Date(oldest.createdAt);
                  const currentDate = new Date(current.createdAt);
                  return currentDate < oldestDate ? current : oldest;
                });
                const isPrimary = patient.patientId === oldestPatient.patientId;
                
                return (
                  <div key={patient.patientId}>
                    {renderPatientCard(patient, true, isPrimary)}
                  </div>
                );
              })}
            </div>

            {/* Match criteria */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-semibold text-gray-700 mb-2">
                Matching Criteria:
              </div>
              {renderMatchCriteria(
                Object.values(expandedGroup.matchCriteria || {})[0]?.criteria || []
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <select
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                onChange={(e) => setSelectedAction({ groupId: group.groupId, action: e.target.value })}
                defaultValue=""
              >
                <option value="" disabled>
                  Choose action...
                </option>
                <option value="merge">Merge duplicates</option>
                <option value="not-duplicate">Mark as not duplicate</option>
              </select>

              {selectedAction?.groupId === group.groupId && selectedAction.action === 'merge' && (
                <>
                  <select
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    id="primary-select"
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Select primary record...
                    </option>
                    {expandedGroup.patients.map((patient, idx) => {
                      // Determine which is the oldest (primary)
                      const oldestPatient = expandedGroup.patients.reduce((oldest, current) => {
                        const oldestDate = new Date(oldest.createdAt);
                        const currentDate = new Date(current.createdAt);
                        return currentDate < oldestDate ? current : oldest;
                      });
                      const isPrimary = patient.patientId === oldestPatient.patientId;
                      
                      return (
                        <option key={patient.patientId} value={patient.patientId}>
                          {patient.name} (Created: {new Date(patient.createdAt).toLocaleDateString()}){isPrimary ? ' - Primary Record' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => {
                      const primarySelect = document.getElementById('primary-select');
                      const primaryPatientId = primarySelect?.value;
                      
                      if (!primaryPatientId || primaryPatientId === '') {
                        setModalMessage('Please select a primary record from the dropdown');
                        setShowErrorModal(true);
                        return;
                      }
                      
                      // Verify the selected ID is actually in the group
                      const isValid = expandedGroup.patients.some(p => p.patientId === primaryPatientId);
                      if (!isValid) {
                        setModalMessage('Selected patient is not in this group. Please try again.');
                        setShowErrorModal(true);
                        return;
                      }
                      
                      handleMerge(group.groupId, primaryPatientId);
                    }}
                    disabled={!mergeCheck.canMerge}
                    className={`px-6 py-2 rounded-lg font-medium transition ${
                      mergeCheck.canMerge
                        ? 'bg-spruce-600 text-white hover:bg-spruce-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Confirm Merge
                  </button>
                </>
              )}

              {selectedAction?.groupId === group.groupId && selectedAction.action === 'not-duplicate' && (
                <button
                  onClick={() => handleMarkAsNotDuplicate(group.groupId)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition"
                >
                  Confirm Not Duplicate
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-spruce-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading duplicate groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/patients')}
          className="text-spruce-600 hover:text-spruce-800 mb-4"
        >
          ← Back to {t.client.plural}
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Duplicate {t.client.plural}</h1>
            <p className="text-gray-600 mt-2">
              Review and resolve potential duplicate records
            </p>
          </div>
          <button
            onClick={triggerScan}
            disabled={loading}
            className="px-4 py-2 bg-spruce-600 text-white rounded-lg font-medium hover:bg-spruce-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <RefreshCw className={loading ? 'animate-spin' : ''} size={16} />
            {loading ? 'Scanning...' : 'Run Scan Now'}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="mb-6 p-4 bg-spruce-50 border border-spruce-200 rounded-lg">
        <div className="flex items-center gap-2">
          <Users className="text-spruce-600" size={20} />
          <span className="font-semibold text-spruce-900">
            {duplicateGroups.length} duplicate groups found
          </span>
          {!isSuperUser && (
            <span className="ml-4 text-sm text-spruce-700">
              (Some merges may require SuperUser permissions)
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Duplicate groups list */}
      <div className="space-y-4">
        {duplicateGroups.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <CheckCircle className="mx-auto text-green-600 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Duplicates Found
            </h3>
            <p className="text-gray-600">
              All {t.client.pluralLower} appear to be unique.
            </p>
          </div>
        ) : (
          duplicateGroups.map((group, index) => renderDuplicateGroup(group, index))
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Success</h3>
            </div>
            <p className="text-gray-600 mb-6">{modalMessage}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  loadDuplicates();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <XCircle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Error</h3>
            </div>
            <p className="text-gray-600 mb-6">{modalMessage}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateDetailPage;